// Money in the forms. The API holds an amount as an integer in minor units (cents). A staff member types
// major units ("12.50"). The conversion uses string arithmetic only: no floating point touches a price.

export const CURRENCY_PATTERN = /^[A-Z]{3}$/;

/** How many digits follow the decimal point for a currency (2 for USD, 0 for JPY, 3 for KWD). */
export function minorUnitDigits(currency: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
      .maximumFractionDigits as number;
  } catch {
    return 2;
  }
}

/** "12.5" with USD is 1250. `null` when the text is not an amount with at most the currency's digits. */
export function parseMajorToMinor(text: string, currency: string): number | null {
  const digits = minorUnitDigits(currency);
  const match = /^(\d{1,12})(?:\.(\d+))?$/.exec(text.trim());
  if (match === null) return null;
  const whole = match[1] as string;
  const fraction = match[2] ?? "";
  if (fraction.length > digits) return null;
  const minor = Number(`${whole}${fraction.padEnd(digits, "0")}`);
  return Number.isSafeInteger(minor) ? minor : null;
}

/** 1250 with USD is "12.50". */
export function minorToMajor(minor: number, currency: string): string {
  const digits = minorUnitDigits(currency);
  if (digits === 0) return String(minor);
  const text = String(minor).padStart(digits + 1, "0");
  return `${text.slice(0, -digits)}.${text.slice(-digits)}`;
}

export function formatMoney(minor: number, currency: string): string {
  return `${currency} ${minorToMajor(minor, currency)}`;
}
